import { FC, useEffect, useMemo, useState } from "react";
import useClientSidePagination from "../../../../../../common/hooks/use-client-side-pagination";
import HeadingComponent from "../../../../../../common/components/heading/heading.component";
import driverContractListingHelper from "../common/driver-contract-listing.helper";
import CardComponent from "../../../../../../common/components/card/card.component";
import PaginationComponent from "../../../../../../common/components/pagination/pagination.component";
import DriverContractListingViewBasicProps from "../common/types/driver-contract-listing-view-basic-props";
import DriverContractListingByTaxiListingItem from "./common/types/driver-contract-listing-by-taxi-listing-item";
import DriverContractListingByTaxiTableComponent from "./table/driver-contract-listing-by-taxi-table.component";
import driverContractListingByTaxiFactory from "./common/driver-contract-listing-by-taxi.factory";
import ListingSortSelectComponent from "../../../../../../common/components/listing/filter/sort/select/listing-sort-select.component";
import DriverContractListingByTaxiFilter from "./common/types/driver-contract-listing-by-taxi-filter";
import driverContractListingByTaxiRouteQueryParamsService from "./common/driver-contract-listing-by-taxi-route-query-params.service";
import DriverContractListingByTaxiFiltersSelectComponent from "./filter/select/driver-contract-listing-by-taxi-listing-filters-select.component";
import DriverContractListingByTaxiSortSelectOption from "./common/types/driver-contract-listing-by-taxi-sort-select-option";
import DriverContractListingByTaxiFiltersBadgeListComponent from "./filter/list/driver-contract-listing-by-taxi-filters-badge-list.component";
import useIsComponentMounted from "../../../../../../common/hooks/use-is-component-mounted";
import useRouteQueryParams from "../../../../../../common/hooks/use-route-query-params";
import DriverContractListingByTaxiRouteQueryParams from "./common/types/driver-contract-listing-by-taxi-route-query-params";
import DriverContractListingByTaxiSortKey from "./common/types/driver-contract-listing-by-taxi-sort-key";
import driverContractListingByTaxiSortHelper from "./common/sort/driver-contract-listing-by-taxi-sort.helper";
import driverContractListingByTaxiFilterService from "./common/filter/driver-contract-listing-by-taxi-filter.service";
import driverContractListingByTaxiSortService from "./common/sort/driver-contract-listing-by-taxi-sort.service";
import { useAppContext } from "../../../../../../context/app.context";
import useDriverContractsList from "../../../../../../common/services/driver/contracts/list/use-driver-contracts-list";
import useAbort from "../../../../../../common/hooks/use-abort";
import DriverContractsListParams from "../../../../../../common/services/driver/contracts/list/driver-contracts-list-params";
import DriverContractsList from "../../../../../../common/services/driver/contracts/list/driver-contracts-list";

type DriverContractListingByTaxiProps = DriverContractListingViewBasicProps;

const DriverContractListingByTaxiComponent: FC<
  DriverContractListingByTaxiProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();
  const isComponentMounted = useIsComponentMounted();

  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<DriverContractListingByTaxiRouteQueryParams>();

  const [listingItems, setListingItems] = useState<
    DriverContractListingByTaxiListingItem[]
  >([]);
  const [filteredListingItems, setFilteredListingItems] = useState<
    DriverContractListingByTaxiListingItem[]
  >([]);

  const driverContractsTaxiDriverList = useDriverContractsList();
  const driverContractsTaxiDriverListAbort = useAbort();

  const [filters, setFilters] = useState<DriverContractListingByTaxiFilter[]>(
    () =>
      driverContractListingByTaxiRouteQueryParamsService.getFilters(
        routeQueryParams,
      ),
  );
  const [selectedSortKey, setSelectedSortKey] =
    useState<DriverContractListingByTaxiSortKey | null>(
      () =>
        driverContractListingByTaxiRouteQueryParamsService.getSortKey(
          routeQueryParams,
        ) ?? null,
    );

  const { pageData, page, pageSize, setPage, setPageSize, totalResults } =
    useClientSidePagination({
      data: filteredListingItems,
      totalResults: filteredListingItems.length,
    });

  const onPageChange = (page: number) => {
    setPage(page);
    window.scroll({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const filteredListingItems =
      driverContractListingByTaxiFilterService.filterListingItems(
        listingItems,
        filters,
      );

    const sortedListingItems =
      driverContractListingByTaxiSortService.sortListingItems(
        filteredListingItems,
        selectedSortKey,
      );

    setFilteredListingItems(sortedListingItems);
  }, [selectedSortKey, JSON.stringify(filters), JSON.stringify(listingItems)]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    setPage(1);
  }, [pageSize, filters]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const queryParams =
      driverContractListingByTaxiRouteQueryParamsService.createRouteQueryParams(
        filters,
        selectedSortKey,
        page,
        pageSize,
      );

    setRouteQueryParams(queryParams);
  }, [filters, selectedSortKey, page, pageSize]);

  const onListingFetchSuccess = (responseData: DriverContractsList) => {
    const listingItems: DriverContractListingByTaxiListingItem[] =
      driverContractListingByTaxiFactory.createListingItems(responseData);

    setListingItems(listingItems);
  };

  const fetchListing = () => {
    if (!props.driverUuid) {
      return;
    }

    const params: DriverContractsListParams = {
      driverUuid: props.driverUuid,
    };

    driverContractsTaxiDriverList.load(
      params,
      driverContractsTaxiDriverListAbort.signal,
    );
  };

  useEffect(() => {
    if (!driverContractsTaxiDriverList.data) {
      return;
    }
    onListingFetchSuccess(driverContractsTaxiDriverList.data);
  }, [driverContractsTaxiDriverList.data]);

  useEffect(() => {
    if (!props.driverUuid) {
      return;
    }

    fetchListing();

    return driverContractsTaxiDriverListAbort.revoke;
  }, [props.driverUuid]);

  const addNewFilter = (newFilter: DriverContractListingByTaxiFilter) => {
    setFilters((curr) => [...curr, newFilter]);
  };

  const deleteFilter = (index: number) => {
    const newFilters = filters.filter((filterItem) => {
      return filters.indexOf(filterItem) !== index;
    });

    setFilters(newFilters);
  };

  const deleteAllFilters = () => {
    setFilters([]);
  };

  const sortSelectOptions: DriverContractListingByTaxiSortSelectOption[] =
    useMemo(
      () => driverContractListingByTaxiSortHelper.getSelectOptions(),
      [selectedAppLanguage],
    );

  const selectedSortSelectOption = useMemo(() => {
    return (
      sortSelectOptions.find((item) => item.value === selectedSortKey) ?? null
    );
  }, [selectedSortKey]);

  return (
    <div className="driver_contract_listing">
      <HeadingComponent
        title={driverContractListingHelper.getHeadingText(props.driverName)}
        actions={[...props.changeViewButtons, props.addContractButton]}
      />
      <div className="driver_contract_listing_tools">
        <DriverContractListingByTaxiFiltersSelectComponent
          filters={filters}
          onAddNewFilter={addNewFilter}
        />
        <ListingSortSelectComponent
          onChange={(option) => setSelectedSortKey(option?.value!)}
          options={sortSelectOptions}
          value={selectedSortSelectOption}
        />
      </div>
      <DriverContractListingByTaxiFiltersBadgeListComponent
        filters={filters}
        onDeleteFilterClick={deleteFilter}
        onDeleteAllFiltersButtonClick={deleteAllFilters}
      />
      <CardComponent classNames={{ root: "mt-4" }}>
        <DriverContractListingByTaxiTableComponent
          listingItems={pageData}
          isError={driverContractsTaxiDriverList.isError}
          isLoading={driverContractsTaxiDriverList.isLoading}
          driverUuid={props.driverUuid}
        />
        <div className="driver_contract_listing__pagination_wrapper">
          <PaginationComponent
            onPageChange={onPageChange}
            onPageSizeChange={setPageSize}
            page={page}
            pageSize={pageSize}
            totalResults={totalResults}
          />
        </div>
      </CardComponent>
    </div>
  );
};

export default DriverContractListingByTaxiComponent;
